import React, { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { useViewRouter } from "../../hooks/useViewRouter";

// Section links (in order shown in the nav RIGHT column)
const sectionLinks = [
  { label: "The Capital Office", view: "capital" },
  { label: "The Dealroom", view: "dealroom" },
  { label: "The Estates", view: "estates" },
  { label: "The Atelier", view: "atelier" },
];
const mobileLinks = [{ label: "Home", view: "home" }, ...sectionLinks];

const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { view, showView, openContactModal } = useViewRouter();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Three-state nav treatment:
  //   • transparent — only on the home view at the top (hero video visible)
  //   • frosted     — on home once scrolled past 80px, and ALWAYS on
  //                   non-home views (no video behind them)
  const transparent = view === "home" && !scrolled;
  const navStyle = transparent
    ? "bg-transparent border-b border-transparent py-5"
    : "bg-[rgba(250,246,239,0.88)] border-b border-[rgba(200,169,90,0.20)] backdrop-blur-[18px] py-3";

  const linkClass = (isActive) =>
    `font-ui text-[10px] uppercase track-label transition-colors duration-300 relative ${
      isActive
        ? "text-num-gold"
        : transparent
          ? "text-[#F5EFE0] hover:text-num-gold"
          : "text-[#1C1410] hover:text-num-gold"
    }`;

  const activeSection = sectionLinks.find((l) => l.view === view);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-500 ${navStyle}`}
      data-testid="site-nav"
    >
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12 flex items-center justify-between gap-6">
        {/* LEFT — clickable wordmark block */}
        <button
          type="button"
          onClick={() => showView("home")}
          className="flex items-center gap-3 flex-shrink-0 focus:outline-none"
          data-testid="nav-wordmark"
        >
          <img
            src="/brand/hnmb-logo.tight.svg"
            alt=""
            aria-hidden="true"
            draggable={false}
            className="filter-parchment block"
            style={{ height: 38, width: "auto" }}
          />
          <span
            className="font-display whitespace-nowrap"
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontWeight: 500,
              fontStyle: "normal",
              fontSize: 18,
              letterSpacing: "0.06em",
              color: transparent ? "#F5EFE0" : "#1C1410",
              transition: "color 0.4s ease",
            }}
          >
            House of Numbardar
          </span>
        </button>

        {/* CENTER — active section name (or tagline on home) */}
        <div className="hidden lg:flex flex-1 items-center justify-center px-6">
          {view === "home" ? (
            <span
              className="font-ui uppercase text-num-gold"
              style={{ fontSize: 9, letterSpacing: "0.52em" }}
            >
              Legacy Since 1795
            </span>
          ) : (
            <span
              className="font-display whitespace-nowrap"
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontWeight: 500,
                fontStyle: "normal",
                fontSize: 20,
                letterSpacing: "0.08em",
                color: "#1C1410",
              }}
              data-testid="nav-section-title"
            >
              {activeSection?.label}
            </span>
          )}
        </div>

        {/* RIGHT — section links + Consult button */}
        <nav className="hidden lg:flex items-center gap-8 flex-shrink-0">
          {sectionLinks.map((l) => (
            <button
              key={l.view}
              type="button"
              onClick={() => showView(l.view)}
              className={linkClass(view === l.view)}
              data-testid={`nav-${l.view}`}
              data-view={l.view}
            >
              {l.label}
              <span
                className={`absolute -bottom-1 left-0 h-px bg-num-gold transition-all duration-300 ${
                  view === l.view ? "w-full" : "w-0"
                }`}
              />
            </button>
          ))}
          <button
            type="button"
            onClick={openContactModal}
            className="btn-gold font-ui text-[10px] uppercase track-label px-6 py-3"
            data-testid="nav-consult-btn"
          >
            Consult the House
          </button>
        </nav>

        {/* Mobile toggle */}
        <button
          className={`lg:hidden p-2 transition-colors duration-300 ${
            transparent ? "text-[#F5EFE0]" : "text-[#1C1410]"
          }`}
          onClick={() => setOpen(true)}
          aria-label="Open menu"
          data-testid="nav-mobile-toggle"
        >
          <Menu size={22} />
        </button>
      </div>

      {/* Mobile overlay */}
      {open && (
        <div
          className="fixed inset-0 z-[110] flex flex-col"
          style={{ backgroundColor: "#1C0606" }}
          data-testid="mobile-menu"
        >
          <div className="flex justify-between items-center px-6 py-5 border-b border-[rgba(200,169,90,0.18)]">
            <div className="flex items-center gap-3">
              <img
                src="/brand/hnmb-logo.tight.svg"
                alt=""
                aria-hidden="true"
                draggable={false}
                className="filter-parchment block"
                style={{ height: 36, width: "auto" }}
              />
              <span className="font-display text-[18px] text-num-cream">
                House of Numbardar
              </span>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-num-cream"
              aria-label="Close menu"
              data-testid="mobile-menu-close"
            >
              <X size={22} />
            </button>
          </div>
          <div className="flex-1 flex flex-col items-center justify-center gap-6">
            {mobileLinks.map((l) => (
              <button
                key={l.view}
                type="button"
                onClick={() => {
                  setOpen(false);
                  showView(l.view);
                }}
                className={`font-display text-[28px] sm:text-[32px] transition-colors ${
                  view === l.view ? "text-num-gold" : "text-num-cream hover:text-num-gold"
                }`}
                data-testid={`mobile-nav-${l.view}`}
              >
                {l.label}
              </button>
            ))}
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                openContactModal();
              }}
              className="btn-gold font-ui text-[11px] uppercase track-label px-7 py-3 mt-4"
              data-testid="mobile-nav-consult"
            >
              Consult the House
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navigation;
